import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePets } from '../context/PetContext';
import { purchaseItem } from '../api/client';
import { ArrowIcon } from '../components/icons';
import './Abilities.css';

const ABILITIES = [
  { id: 'head_mount', name: 'Head Mount', icon: '👑', level: 3, treats: 150,
    description: 'Pet rides on top of your head.' },
  { id: 'night_vision', name: 'Night Vision', icon: '👁', level: 4, treats: 200,
    description: 'Pet glows and gives you night vision.' },
  { id: 'ore_radar', name: 'Ore Radar', icon: '💎', level: 5, treats: 300,
    description: 'Pet detects nearby ores with glowing particles.' },
  { id: 'swift_companion', name: 'Swift Companion', icon: '⚡', level: 6, treats: 150,
    description: 'Pet gives you Speed I while following.' },
  { id: 'pet_satchel', name: 'Pet Satchel', icon: '🎒', level: 8, treats: 250,
    description: '6-slot portable inventory on your pet.' },
  { id: 'combat_buddy', name: 'Combat Buddy', icon: '⚔', level: 10, treats: 350,
    description: 'Pet attacks hostile mobs near you.' },
];

export default function Abilities() {
  const { slot } = useParams();
  const slotNum = parseInt(slot, 10);
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { pets, userData, equipCosmetic, refreshData } = usePets();
  const [purchasing, setPurchasing] = useState(null);
  const [message, setMessage] = useState('');

  const pet = pets.find((p) => (p.slot_index ?? p.slotIndex) === slotNum);
  const petLevel = pet?.level || 1;
  const unlockedAbilities = pet?.unlocked_abilities || pet?.unlockedAbilities || [];
  const activeAbilities = pet?.active_abilities || pet?.activeAbilities || [];
  const treats = userData?.treats ?? 0;

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  if (!pet) {
    return (
      <div className="abilities-page">
        <div className="container">
          <p>No pet in this slot.</p>
          <button className="btn btn-secondary" onClick={() => navigate('/pets')}>Back to Kennel</button>
        </div>
      </div>
    );
  }

  const handleToggle = async (abilityId) => {
    // Toggle ability active/inactive
    const newActive = activeAbilities.includes(abilityId)
      ? activeAbilities.filter(a => a !== abilityId)
      : [...activeAbilities, abilityId];

    // Update via equipCosmetic-like push (reuse the sync mechanism)
    const updatedPets = pets.map(p => {
      if ((p.slot_index ?? p.slotIndex) !== slotNum) return p;
      return { ...p, activeAbilities: newActive, active_abilities: newActive };
    });

    // Push update
    const { saveUserData } = await import('../api/client');
    const newData = { ...userData, pets: updatedPets.map(p => ({
      petTypeId: p.pet_type_id || p.petTypeId,
      petName: p.pet_name || p.petName,
      traitIds: p.trait_ids || p.traitIds || [],
      slotIndex: p.slot_index ?? p.slotIndex ?? 0,
      active: p.active || false,
      tiny: p.tiny || false,
      variantId: p.variant_id || p.variantId || null,
      tintColor: p.tint_color ?? p.tintColor ?? 0,
      level: p.level || 1, xp: p.xp || 0,
      hunger: p.hunger ?? 100, happiness: p.happiness ?? 100, energy: p.energy ?? 100,
      mood: p.mood || 'CONTENT',
      equippedCosmetics: p.equipped_cosmetics || p.equippedCosmetics || {},
      unlockedAbilities: p.unlocked_abilities || p.unlockedAbilities || [],
      activeAbilities: p.active_abilities || p.activeAbilities || [],
      emoteShortcuts: p.emote_shortcuts || p.emoteShortcuts || [],
      adoptedAt: p.adopted_at || p.adoptedAt || 0,
      lastActiveAt: p.last_active_at || p.lastActiveAt || 0,
      lastModifiedAt: Date.now(),
    }))};
    await saveUserData(newData);
    refreshData();
  };

  const handleUnlockFree = async (abilityId) => {
    const newUnlocked = [...unlockedAbilities, abilityId];
    const newActive = [...activeAbilities, abilityId];

    const updatedPets = pets.map(p => {
      if ((p.slot_index ?? p.slotIndex) !== slotNum) return p;
      return {
        ...p,
        unlockedAbilities: newUnlocked, unlocked_abilities: newUnlocked,
        activeAbilities: newActive, active_abilities: newActive,
      };
    });

    const { saveUserData } = await import('../api/client');
    const newData = { ...userData, pets: updatedPets.map(p => ({
      petTypeId: p.pet_type_id || p.petTypeId,
      petName: p.pet_name || p.petName,
      slotIndex: p.slot_index ?? p.slotIndex ?? 0,
      active: p.active || false, tiny: p.tiny || false,
      variantId: p.variant_id || p.variantId || null,
      tintColor: p.tint_color ?? p.tintColor ?? 0,
      level: p.level || 1, xp: p.xp || 0,
      hunger: p.hunger ?? 100, happiness: p.happiness ?? 100, energy: p.energy ?? 100,
      mood: p.mood || 'CONTENT',
      equippedCosmetics: p.equipped_cosmetics || p.equippedCosmetics || {},
      unlockedAbilities: p.unlocked_abilities || p.unlockedAbilities || [],
      activeAbilities: p.active_abilities || p.activeAbilities || [],
      traitIds: p.trait_ids || p.traitIds || [],
      emoteShortcuts: p.emote_shortcuts || p.emoteShortcuts || [],
      adoptedAt: p.adopted_at || p.adoptedAt || 0,
      lastActiveAt: p.last_active_at || p.lastActiveAt || 0,
      lastModifiedAt: Date.now(),
    }))};
    await saveUserData(newData);
    refreshData();
    setMessage(`${ABILITIES.find(a => a.id === abilityId)?.name} unlocked!`);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleBuyWithTreats = async (abilityId, cost) => {
    setPurchasing(abilityId);
    try {
      const res = await purchaseItem(abilityId);
      if (res.data.success) {
        await handleUnlockFree(abilityId);
        setMessage(`Purchased! ${res.data.treats_remaining} treats remaining.`);
      } else {
        setMessage(res.data.message);
      }
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Purchase failed.');
    }
    setPurchasing(null);
    setTimeout(() => setMessage(''), 4000);
  };

  return (
    <div className="abilities-page">
      <div className="container">
        <button className="back-btn" onClick={() => navigate(`/pets/${slotNum}`)}>
          <ArrowIcon size={18} /> Back to {pet.pet_name || pet.petName}
        </button>

        <div className="abilities-header">
          <h1>{pet.pet_name || pet.petName}'s Abilities</h1>
          <div className="abilities-meta">
            <span className="pet-level-badge">Level {petLevel}</span>
            <span className="treat-balance">🦴 {treats} Treats</span>
          </div>
        </div>

        {message && <div className="abilities-message">{message}</div>}

        <div className="abilities-grid">
          {ABILITIES.map((ability) => {
            const unlocked = unlockedAbilities.includes(ability.id);
            const active = activeAbilities.includes(ability.id);
            const canUnlockFree = petLevel >= ability.level;

            return (
              <div key={ability.id} className={`ability-card card ${unlocked ? 'unlocked' : 'locked'} ${active ? 'active' : ''}`}>
                <div className="ability-icon">{ability.icon}</div>
                <div className="ability-info">
                  <h3>{ability.name}</h3>
                  <p>{ability.description}</p>

                  {!unlocked && (
                    <div className="ability-unlock-info">
                      <div className="ability-progress">
                        <div className="ability-progress-bar"
                             style={{ width: `${Math.min(100, (petLevel / ability.level) * 100)}%` }} />
                      </div>
                      <span className="ability-level-req">
                        Level {ability.level} {canUnlockFree && '- Ready!'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="ability-actions">
                  {unlocked ? (
                    <button
                      className={`btn ${active ? 'btn-active' : 'btn-ghost'} btn-sm`}
                      onClick={() => handleToggle(ability.id)}
                    >
                      {active ? 'ON' : 'OFF'}
                    </button>
                  ) : canUnlockFree ? (
                    <button className="btn btn-secondary btn-sm" onClick={() => handleUnlockFree(ability.id)}>
                      Unlock Free
                    </button>
                  ) : (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleBuyWithTreats(ability.id, ability.treats)}
                      disabled={purchasing === ability.id}
                    >
                      {purchasing === ability.id ? '...' : `🦴 ${ability.treats}`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
